/**
 * Date / time formatters.
 *
 * Conventions:
 *   • Wire times are ALWAYS UTC ISO-8601 with `+00:00` or `Z` (see ./api.ts).
 *   • Display times use the user-selected timezone (default Asia/Kolkata).
 *   • Markets (NSE/BSE) operate in IST; we surface both UTC and IST when
 *     they differ to support operators working across desks.
 *
 * Two-axis formatting:
 *   • `formatDateTime` is the workhorse — strict ISO display.
 *   • `formatRelative` is for "5m ago" / "in 12s" labels (cmdk results,
 *     remediation SLA pills, audit log timestamps in dense tables).
 *
 * Market-session helpers:
 *   • `marketSessionAt(ts, market)` returns 'pre-open' | 'open' | 'closed'.
 *     Used by Mission Control's market pills and the Pre-Open checklist.
 */

import { differenceInSeconds, isAfter, isBefore, parseISO } from 'date-fns';
import { format as formatTz, formatInTimeZone, toZonedTime } from 'date-fns-tz';

import type { ISODateTime } from '@/types/money';

/* ============================================================================
 * Common timezones (operator-friendly aliases for IANA strings)
 * ============================================================================ */

export const COMMON_TIMEZONES = ['Asia/Kolkata', 'UTC', 'America/New_York', 'Europe/London', 'Asia/Singapore', 'Asia/Dubai'] as const;
export type CommonTimezone = (typeof COMMON_TIMEZONES)[number];

export const DEFAULT_DISPLAY_TZ: CommonTimezone = 'Asia/Kolkata';

/* ============================================================================
 * Pattern presets — keep them small and named so audit/export views render
 * consistently across pages.
 * ============================================================================ */

export const DATETIME_PATTERNS = {
  /** `12 May 2026, 09:13:45 IST`  — default verbose */
  full: 'dd MMM yyyy, HH:mm:ss zzz',
  /** `12 May 2026, 09:13 IST` */
  minutes: 'dd MMM yyyy, HH:mm zzz',
  /** `09:13:45.123` */
  timeMs: 'HH:mm:ss.SSS',
  /** `09:13:45` */
  time: 'HH:mm:ss',
  /** `09:13` */
  timeShort: 'HH:mm',
  /** `12 May` */
  monthDay: 'dd MMM',
  /** `2026-05-12` */
  isoDate: 'yyyy-MM-dd',
  /** `2026-05-12 09:13:45 +0530` */
  isoLike: 'yyyy-MM-dd HH:mm:ss xx',
} as const;
export type DateTimePattern = keyof typeof DATETIME_PATTERNS;

export interface FormatDateTimeOptions {
  /** IANA timezone for display. Default `Asia/Kolkata`. */
  tz?: string;
  /** Preset name or raw date-fns-tz pattern string. */
  pattern?: DateTimePattern | string;
}

export const formatDateTime = (
  ts: ISODateTime | Date,
  opts: FormatDateTimeOptions = {},
): string => {
  const tz = opts.tz ?? DEFAULT_DISPLAY_TZ;
  const pattern = opts.pattern ?? 'full';
  const fmt = DATETIME_PATTERNS[pattern as DateTimePattern] ?? pattern;
  const date = typeof ts === 'string' ? parseISO(ts) : ts;
  return formatInTimeZone(date, tz, fmt);
};

/** Render the same instant in both UTC and the configured display TZ, suitable
 *  for an audit log row that must show both for compliance. */
export const formatDateTimeBilingual = (
  ts: ISODateTime | Date,
  displayTz: string = DEFAULT_DISPLAY_TZ,
): { display: string; utc: string; tz: string } => ({
  display: formatDateTime(ts, { tz: displayTz, pattern: 'isoLike' }),
  utc: formatDateTime(ts, { tz: 'UTC', pattern: 'isoLike' }),
  tz: displayTz,
});

/* ============================================================================
 * Relative time — operator-friendly
 *   "now", "5s ago", "12m ago", "in 1h 23m", "in 2d 4h"
 *
 * Stable across the second boundary: callers should pass a stable `now` to
 * keep React renders from constantly diffing.
 * ============================================================================ */

export interface FormatRelativeOptions {
  /** Reference "now" timestamp. Defaults to `Date.now()`. */
  now?: Date | number;
  /** Granularity: 'seconds' (default) | 'minutes' (no s-precision). */
  granularity?: 'seconds' | 'minutes';
  /** When > 0, returns `formatDateTime(...)` if delta exceeds threshold (seconds). */
  absoluteAfterSec?: number;
}

export const formatRelative = (
  ts: ISODateTime | Date,
  opts: FormatRelativeOptions = {},
): string => {
  const date = typeof ts === 'string' ? parseISO(ts) : ts;
  const nowDate = opts.now === undefined ? new Date() : typeof opts.now === 'number' ? new Date(opts.now) : opts.now;
  const deltaSec = differenceInSeconds(date, nowDate);
  const absSec = Math.abs(deltaSec);

  if (opts.absoluteAfterSec !== undefined && absSec > opts.absoluteAfterSec) {
    return formatDateTime(date);
  }

  const future = deltaSec > 0;
  const fmt = (n: number, unit: string) => `${n}${unit}`;

  if (absSec < 5) {
    return 'now';
  }
  if (absSec < 60) {
    if (opts.granularity === 'minutes') {
      return future ? 'in <1m' : '<1m ago';
    }
    const part = fmt(absSec, 's');
    return future ? `in ${part}` : `${part} ago`;
  }
  if (absSec < 3600) {
    const m = Math.floor(absSec / 60);
    const s = absSec % 60;
    const part = opts.granularity === 'minutes' || s === 0 ? fmt(m, 'm') : `${m}m ${s}s`;
    return future ? `in ${part}` : `${part} ago`;
  }
  if (absSec < 86_400) {
    const h = Math.floor(absSec / 3600);
    const m = Math.floor((absSec % 3600) / 60);
    const part = m === 0 ? fmt(h, 'h') : `${h}h ${m}m`;
    return future ? `in ${part}` : `${part} ago`;
  }
  const d = Math.floor(absSec / 86_400);
  const h = Math.floor((absSec % 86_400) / 3600);
  const part = h === 0 ? fmt(d, 'd') : `${d}d ${h}h`;
  return future ? `in ${part}` : `${part} ago`;
};

/* ============================================================================
 * Stale / freshness label
 * ============================================================================ */

export interface StalenessOptions {
  /** Seconds before a sample is considered "stale". Default 30s. */
  staleAfterSec?: number;
  /** Seconds before a sample is considered "expired". Default 120s. */
  expiredAfterSec?: number;
  now?: Date | number;
}

export type StalenessLevel = 'fresh' | 'stale' | 'expired';

export const stalenessOf = (
  ts: ISODateTime | Date | null,
  opts: StalenessOptions = {},
): StalenessLevel => {
  if (ts === null) {
    return 'expired';
  }
  const { staleAfterSec = 30, expiredAfterSec = 120 } = opts;
  const nowMs = opts.now === undefined ? Date.now() : typeof opts.now === 'number' ? opts.now : opts.now.getTime();
  const date = typeof ts === 'string' ? parseISO(ts) : ts;
  const ageSec = (nowMs - date.getTime()) / 1000;
  if (ageSec > expiredAfterSec) {
    return 'expired';
  }
  if (ageSec > staleAfterSec) {
    return 'stale';
  }
  return 'fresh';
};

/* ============================================================================
 * Market sessions
 *
 * NSE / BSE official cash-equity hours: 09:15 IST – 15:30 IST, with pre-open
 * 09:00 IST – 09:15 IST. F&O extends slightly per segment; this helper sticks
 * to the cash window. Holiday handling is deferred to the market data tier
 * (backend ask: `GET /market/holidays?market=NSE&fy=2026`).
 * ============================================================================ */

export const MARKETS = ['NSE', 'BSE', 'NYSE', 'NASDAQ', 'CME', 'BINANCE_PERP'] as const;
export type MarketCode = (typeof MARKETS)[number];

interface MarketWindow {
  tz: string;
  preOpenStart: { h: number; m: number };
  open: { h: number; m: number };
  close: { h: number; m: number };
}

const MARKET_WINDOWS: Record<MarketCode, MarketWindow | null> = {
  NSE: { tz: 'Asia/Kolkata', preOpenStart: { h: 9, m: 0 }, open: { h: 9, m: 15 }, close: { h: 15, m: 30 } },
  BSE: { tz: 'Asia/Kolkata', preOpenStart: { h: 9, m: 0 }, open: { h: 9, m: 15 }, close: { h: 15, m: 30 } },
  // US markets: regular trading hours 09:30 – 16:00 ET; pre-market 04:00 – 09:30.
  NYSE: { tz: 'America/New_York', preOpenStart: { h: 4, m: 0 }, open: { h: 9, m: 30 }, close: { h: 16, m: 0 } },
  NASDAQ: { tz: 'America/New_York', preOpenStart: { h: 4, m: 0 }, open: { h: 9, m: 30 }, close: { h: 16, m: 0 } },
  CME: { tz: 'America/Chicago', preOpenStart: { h: 17, m: 0 }, open: { h: 18, m: 0 }, close: { h: 17, m: 0 } },
  BINANCE_PERP: null, // 24/7 — always open
};

export type MarketSession = 'pre-open' | 'open' | 'closed' | 'always-open';

export const marketSessionAt = (
  ts: ISODateTime | Date,
  market: MarketCode,
): MarketSession => {
  const cfg = MARKET_WINDOWS[market];
  if (cfg === null) {
    return 'always-open';
  }
  const date = typeof ts === 'string' ? parseISO(ts) : ts;
  const zoned = toZonedTime(date, cfg.tz);
  const localStr = formatTz(zoned, 'HH:mm', { timeZone: cfg.tz });
  const [hourStr, minStr] = localStr.split(':');
  const h = Number(hourStr);
  const m = Number(minStr);
  const minutes = h * 60 + m;
  const preOpenMin = cfg.preOpenStart.h * 60 + cfg.preOpenStart.m;
  const openMin = cfg.open.h * 60 + cfg.open.m;
  const closeMin = cfg.close.h * 60 + cfg.close.m;

  if (closeMin > openMin) {
    if (minutes >= preOpenMin && minutes < openMin) {
      return 'pre-open';
    }
    if (minutes >= openMin && minutes < closeMin) {
      return 'open';
    }
    return 'closed';
  }
  // Wraps midnight (CME-style overnight session)
  if (minutes >= openMin || minutes < closeMin) {
    return 'open';
  }
  if (minutes >= preOpenMin && minutes < openMin) {
    return 'pre-open';
  }
  return 'closed';
};

/* ============================================================================
 * Utility: parse, dual-render, compare
 * ============================================================================ */

export const isInRange = (ts: ISODateTime | Date, start: Date, end: Date): boolean => {
  const date = typeof ts === 'string' ? parseISO(ts) : ts;
  return !isBefore(date, start) && !isAfter(date, end);
};
